/**
 * InlineSectionEditor — heading-delimited inline section editing over a Markdown document.
 *
 * Extracted from Step8ReviewPreview's LeasePreviewViewer so the lease wizard and the amendment
 * flow share one editor (per-section Edit / Modified badge / Reset to Original, scoped lease CSS).
 * The Edit / Reset controls are always visible labelled buttons (never hover-revealed).
 *
 *  - `value` is the current Markdown; `original` is the unedited render (drives Modified state).
 *  - `onChange(fullMd)` receives the whole document after a section save (or an internal reset).
 *  - `onReset(index)` is optional. When provided, Reset buttons delegate to it (the wizard wraps
 *    the reset in a confirmation dialog); otherwise the section is restored here and onChange fires.
 *  - `testIdPrefix` is optional: it namespaces test ids so several editors can coexist on one page
 *    (the wizard passes nothing and keeps its original test ids).
 */

import { useEffect, useRef, useState } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { MarkdownThemeScope } from '../MarkdownThemeScope';
import '../rules/md-editor-overrides.css';
import scopedLeaseDocumentCss from 'virtual:lease-document-scoped-css';
import { Button } from '../ui/button';
import { splitIntoSections, spliceSection, isContentNew } from '../../pages/lease-wizard/steps/section-utils';
import { renderSectionsSequentially } from './lease-render';

export interface InlineSectionEditorProps {
  original: string;
  value: string;
  onChange: (newValue: string) => void;
  onReset?: (sectionIndex: number) => void;
  testIdPrefix?: string;
}

export function InlineSectionEditor({ original, value, onChange, onReset, testIdPrefix = '' }: InlineSectionEditorProps) {
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [sectionDraft, setSectionDraft] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  // Index of the section whose editor just closed — focus returns to its Edit button.
  const returnFocusIndex = useRef<number | null>(null);

  useEffect(() => {
    if (editingSectionIndex !== null || returnFocusIndex.current === null) return;
    const i = returnFocusIndex.current;
    returnFocusIndex.current = null;
    containerRef.current
      ?.querySelector<HTMLElement>(`[data-testid="${testIdPrefix}edit-section-button-${i}"]`)
      ?.focus();
  }, [editingSectionIndex, testIdPrefix]);

  const sections = splitIntoSections(value);
  const origSections = original ? splitIntoSections(original) : [];
  // Render each section sequentially so heading counter state threads through correctly.
  const renderedSections = renderSectionsSequentially(sections);

  function handleEditSection(index: number) {
    setSectionDraft(sections[index]?.content ?? '');
    setEditingSectionIndex(index);
  }

  function handleSectionSave(index: number, newMd: string) {
    const section = sections[index];
    if (!section) return;
    onChange(spliceSection(value, section.startIndex, section.endIndex, newMd));
    returnFocusIndex.current = index;
    setEditingSectionIndex(null);
    setSectionDraft('');
  }

  function handleSectionCancel() {
    returnFocusIndex.current = editingSectionIndex;
    setEditingSectionIndex(null);
    setSectionDraft('');
  }

  function handleResetSection(index: number) {
    if (onReset) {
      onReset(index);
      return;
    }
    const origSection = origSections[index];
    const currentSection = sections[index];
    if (!origSection || !currentSection) return;
    onChange(spliceSection(value, currentSection.startIndex, currentSection.endIndex, origSection.content));
  }

  return (
    <>
      {/* Inject scoped CSS — build-time constant, not user input */}
      <style dangerouslySetInnerHTML={{ __html: scopedLeaseDocumentCss }} />

      <div ref={containerRef} className="lease-document px-2">
        {sections.map((section, i) => {
          const isEditing = editingSectionIndex === i;
          const modified = original ? isContentNew(section.content, origSections) : false;
          const sectionHtml = renderedSections[i]?.html ?? '';
          const before = renderedSections[i]?.beforeCounters ?? {
            section: 0,
            subsection: 0,
            subsubsection: 0,
          };

          return (
            <div
              key={section.startIndex}
              className="lease-section-wrapper"
              style={{
                counterReset: `section ${before.section} subsection ${before.subsection} subsubsection ${before.subsubsection}`,
              }}
            >
              {isEditing ? (
                /* Section editor */
                <div className="p-4 border border-primary/20 rounded-lg bg-card my-2">
                  <MarkdownThemeScope paper>
                    <MDEditor
                      value={sectionDraft}
                      onChange={(val) => setSectionDraft(val ?? '')}
                      height={300}
                      preview="edit"
                      autoFocus
                      textareaProps={{
                        'aria-label': `Edit section: ${section.heading ?? 'Introduction'}`,
                      }}
                    />
                  </MarkdownThemeScope>
                  <div className="flex gap-2 justify-end mt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`${testIdPrefix}cancel-section-button-${i}`}
                      onClick={handleSectionCancel}
                      className="min-h-[44px] text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      data-testid={`${testIdPrefix}save-section-button-${i}`}
                      onClick={() => handleSectionSave(i, sectionDraft)}
                      className="min-h-[44px] text-xs"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Edit affordance: always visible (no hover/focus reveal) so first-time users find it.
                      One labelled control row at the top of every section, all breakpoints. font-sans: the
                      scoped lease CSS sets a serif document font on this container. */}
                  <div
                    className="flex flex-wrap items-center justify-end gap-2 mb-1 font-sans"
                    data-testid={`${testIdPrefix}section-actions-${i}`}
                  >
                    {modified && (
                      <span
                        data-testid={`${testIdPrefix}section-modified-badge-${i}`}
                        className="text-xs text-warning font-medium mr-auto"
                      >
                        Modified
                      </span>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`${testIdPrefix}edit-section-button-${i}`}
                      // Accessible name starts with the visible label (WCAG 2.5.3 label-in-name) and
                      // adds the section so several identical buttons stay distinguishable.
                      aria-label={`Edit this section: ${section.heading ?? 'Introduction'}`}
                      onClick={() => handleEditSection(i)}
                      className="min-h-[44px] text-xs px-3"
                    >
                      Edit this section
                    </Button>
                    {modified && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid={`${testIdPrefix}reset-section-button-${i}`}
                        aria-label={`Reset to original: ${section.heading ?? 'Introduction'}`}
                        onClick={() => handleResetSection(i)}
                        className="min-h-[44px] text-xs px-3"
                      >
                        Reset to original
                      </Button>
                    )}
                  </div>

                  {/* Rendered section HTML — sanitized via DOMPurify above */}
                  {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
                  <div dangerouslySetInnerHTML={{ __html: sectionHtml }} />

                </>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
